import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Briefcase, Building2, MapPin, Network, Plus, Pencil, Trash2, Layers,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDireccionCompleta, fmtFecha, fmtHora, ROL_LABEL } from "@/lib/format";
import DireccionEspanaFields from "@/components/DireccionEspanaFields";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  HorarioTramosEditor, tramosFromHorario, horarioParaGuardarTramos, resumenHorarioTramos, tramoDefecto, erroresSolapeTramos,
  aplicarVentanasFlexiblesATramos, reemplazarTramosDesde,
} from "@/components/HorarioTramosEditor";
import { HorarioModoConfig, modoConfigDefecto } from "@/components/HorarioModoConfig";
import { modoConfigFromHorario, resumenModoHorario, mensajeObjetivoFlexible, MODO_FLEXIBLE, MODO_PATRON_TURNOS } from "@/lib/horarioModos";
import { HORARIO_FLEXIBLE_DEFECTO } from "@/components/HorarioEditor";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { orgHeaderStyle } from "@/lib/uiTheme";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const COLORES = ["#6366f1", "#22c55e", "#f97316", "#8b5cf6", "#0ea5e9", "#ec4899", "#eab308", "#64748b"];

const ESTADO = {
  presente: { dot: "bg-emerald-500", badge: "border-emerald-200 bg-emerald-100 text-emerald-800", label: "Presente" },
  fuera: { dot: "bg-slate-300", badge: "border-slate-200 bg-slate-100 text-slate-600", label: "Fuera" },
  ausente: { dot: "bg-amber-500", badge: "border-amber-200 bg-amber-100 text-amber-800", label: "Ausente" },
};

function FilaTrabajador({ t, accent }) {
  const cfg = ESTADO[t.estado];
  const iniciales = t.nombre.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-white/60" data-testid={`organizacion-trabajador-${t.id}`}>
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm" style={{ backgroundColor: accent || "#64748b" }}>
            {iniciales}
          </div>
          <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${cfg.dot}`} />
        </div>
        <div>
          <p className="text-sm font-medium leading-tight">{t.nombre}</p>
          <p className="text-[11px] text-zinc-500">{t.puesto || ROL_LABEL[t.rol]}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {t.ultimo_fichaje && <span className="text-xs text-zinc-400">{fmtHora(t.ultimo_fichaje)}</span>}
        <Badge variant="outline" className={cfg.badge}>{cfg.label}</Badge>
      </div>
    </div>
  );
}

function SelectorColor({ value, onChange, size = "md" }) {
  const cls = size === "sm" ? "h-6 w-6" : "h-7 w-7";
  return (
    <div className="flex flex-wrap gap-1.5">
      {COLORES.map((c) => (
        <button
          key={c}
          type="button"
          className={`${cls} rounded-full border-2 ${value === c ? "border-zinc-900" : "border-transparent"}`}
          style={{ backgroundColor: c }}
          onClick={() => onChange(c)}
        />
      ))}
    </div>
  );
}

export default function Organizacion() {
  const { user } = useAuth();
  const esAdmin = user?.rol === "admin";
  const [data, setData] = useState(null);
  const [turnos, setTurnos] = useState([]);

  const [dlgCentro, setDlgCentro] = useState(false);
  const [dlgDep, setDlgDep] = useState(false);
  const [dlgPuesto, setDlgPuesto] = useState(false);
  const [editCentroId, setEditCentroId] = useState(null);
  const [editDepId, setEditDepId] = useState(null);
  const [editPuestoId, setEditPuestoId] = useState(null);

  const [centroForm, setCentroForm] = useState({
    nombre: "", direccion: "", poblacion: "", provincia: "", codigo_postal: "",
    color: COLORES[0], usarHorario: false, motivo_horario: "",
  });
  const [centroTramos, setCentroTramos] = useState([tramoDefecto()]);
  const [centroModo, setCentroModo] = useState(modoConfigDefecto());
  const [depForm, setDepForm] = useState({
    nombre: "",
    centro_id: "",
    color: COLORES[1],
    motivo_horario: "",
    usarHorario: false,
    empleadosAplicarIds: [],
  });
  const [depTramos, setDepTramos] = useState([tramoDefecto()]);
  const [depModo, setDepModo] = useState(modoConfigDefecto());
  const [puestoForm, setPuestoForm] = useState({ nombre: "", departamento_id: "", color: COLORES[4] });

  const cargar = useCallback(async () => {
    try {
      const reqs = [api.get("/organizacion")];
      if (esAdmin) reqs.push(api.get("/turnos"));
      const results = await Promise.all(reqs);
      setData(results[0].data);
      if (results[1]) setTurnos(results[1].data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [esAdmin]);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, [cargar]);

  const abrirCentro = (centro = null) => {
    setEditCentroId(centro?.id ?? null);
    if (centro) {
      setCentroForm({
        nombre: centro.nombre,
        direccion: centro.direccion || "",
        poblacion: centro.poblacion || "",
        provincia: centro.provincia || "",
        codigo_postal: centro.codigo_postal || "",
        color: centro.color,
        usarHorario: !!centro.horario,
        motivo_horario: "",
      });
      setCentroTramos(tramosFromHorario(centro.horario));
      setCentroModo(modoConfigFromHorario(centro.horario));
    } else {
      setCentroForm({
        nombre: "", direccion: "", poblacion: "", provincia: "", codigo_postal: "",
        color: COLORES[0], usarHorario: false, motivo_horario: "",
      });
      setCentroTramos([tramoDefecto()]);
      setCentroModo(modoConfigDefecto());
    }
    setDlgCentro(true);
  };

  const horarioCentroSeleccionado = (centroId) => {
    const c = (data?.centros || []).find((x) => x.id === Number(centroId));
    return c?.horario || null;
  };

  const abrirDep = (centroId, dep = null) => {
    setEditDepId(dep?.id ?? null);
    const cid = String(dep?.centro_id || centroId || "");
    if (dep) {
      const propio = dep.horario != null;
      setDepForm({
        nombre: dep.nombre,
        centro_id: cid,
        color: dep.color,
        motivo_horario: "",
        usarHorario: propio,
        empleadosAplicarIds: [],
      });
      const origen = propio ? dep.horario : (dep.horario_efectivo || horarioCentroSeleccionado(cid));
      setDepTramos(tramosFromHorario(origen));
      setDepModo(modoConfigFromHorario(origen));
    } else {
      const origen = horarioCentroSeleccionado(cid);
      setDepForm({
        nombre: "",
        centro_id: cid,
        color: COLORES[1],
        motivo_horario: "",
        usarHorario: false,
        empleadosAplicarIds: [],
      });
      setDepTramos(tramosFromHorario(origen));
      setDepModo(modoConfigFromHorario(origen));
    }
    setDlgDep(true);
  };

  const onToggleHorarioDep = (v) => {
    if (v) {
      // Parte del horario del centro como borrador; el usuario puede cambiar a flexible
      // y sustituir el tramo futuro (p. ej. 01/10) sin heredar franjas fijas.
      const origen = horarioCentroSeleccionado(depForm.centro_id);
      setDepTramos(tramosFromHorario(origen));
      setDepModo(modoConfigFromHorario(origen));
    }
    setDepForm({ ...depForm, usarHorario: v });
  };

  const onChangeDepModo = (cfg) => {
    const eraFlexible = depModo.modo === MODO_FLEXIBLE;
    setDepModo(cfg);
    if (cfg.modo === MODO_FLEXIBLE && !eraFlexible) {
      // Ampliar ventanas de tramos futuros a 06:00–22:00 (fichar cuando quieran).
      setDepTramos((prev) => aplicarVentanasFlexiblesATramos(prev, { soloFuturos: true }));
    }
  };

  const sustituirFlexibleDesde = (desdeIso) => {
    const futuro = (depTramos || []).find((t) => (t.desde || "") >= new Date().toISOString().slice(0, 10));
    const desde = desdeIso || futuro?.desde || "2026-10-01";
    setDepModo((prev) => ({
      ...prev,
      modo: MODO_FLEXIBLE,
      flexible: prev.flexible || modoConfigDefecto().flexible,
    }));
    setDepTramos((prev) =>
      reemplazarTramosDesde(prev, desde, HORARIO_FLEXIBLE_DEFECTO, "Horario flexible")
    );
  };

  const fechaSugeridaFlexible = useMemo(() => {
    const hoy = new Date().toISOString().slice(0, 10);
    const futuro = (depTramos || []).find((t) => (t.desde || "") >= hoy);
    return futuro?.desde || "2026-10-01";
  }, [depTramos]);

  const abrirPuesto = (depId, puesto = null) => {
    setEditPuestoId(puesto?.id ?? null);
    setPuestoForm(puesto
      ? { nombre: puesto.nombre, departamento_id: String(depId), color: puesto.color }
      : { nombre: "", departamento_id: String(depId), color: COLORES[4] });
    setDlgPuesto(true);
  };

  const saveCentro = async () => {
    if (centroForm.usarHorario && centroModo.modo !== MODO_PATRON_TURNOS) {
      const errs = erroresSolapeTramos(centroTramos);
      if (errs.length) {
        toast.error(errs[0]);
        return;
      }
    }
    if (centroForm.usarHorario) {
      const avisoObjetivo = mensajeObjetivoFlexible(centroModo);
      if (avisoObjetivo && !window.confirm(avisoObjetivo)) return;
    }
    try {
      const payload = {
        nombre: centroForm.nombre,
        direccion: centroForm.direccion,
        poblacion: centroForm.poblacion,
        provincia: centroForm.provincia,
        codigo_postal: centroForm.codigo_postal,
        color: centroForm.color,
        horario: centroForm.usarHorario ? horarioParaGuardarTramos(centroTramos, centroModo) : null,
        motivo_horario: (centroForm.motivo_horario || "").trim(),
      };
      if (editCentroId) {
        const { data: res } = await api.put(`/centros/${editCentroId}`, payload);
        if (res.fecha_vigencia_horario) {
          const f = new Date(`${res.fecha_vigencia_horario}T12:00:00`).toLocaleDateString("es-ES");
          toast.success(`Centro actualizado. El nuevo horario aplica a partir del ${f}.`);
        } else {
          toast.success("Centro actualizado");
        }
      } else {
        await api.post("/centros", payload);
        toast.success("Centro creado");
      }
      setDlgCentro(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const empleadosConHorarioPropioDep = useMemo(() => {
    if (!editDepId || !data?.centros) return [];
    const out = [];
    for (const c of data.centros) {
      for (const d of c.departamentos || []) {
        if (d.id !== editDepId) continue;
        for (const p of d.puestos || []) {
          for (const t of p.trabajadores || []) {
            if (t.horario_propio) out.push({ id: t.id, nombre: t.nombre });
          }
        }
        for (const t of d.sin_puesto || []) {
          if (t.horario_propio) out.push({ id: t.id, nombre: t.nombre });
        }
      }
    }
    return out;
  }, [data, editDepId]);

  const toggleEmpleadoAplicar = (id) => {
    const cur = depForm.empleadosAplicarIds || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setDepForm({ ...depForm, empleadosAplicarIds: next });
  };

  const toggleTodosEmpleadosAplicar = (marcar) => {
    setDepForm({
      ...depForm,
      empleadosAplicarIds: marcar ? empleadosConHorarioPropioDep.map((e) => e.id) : [],
    });
  };

  const saveDep = async () => {
    if (depForm.usarHorario && depModo.modo !== MODO_PATRON_TURNOS) {
      const errs = erroresSolapeTramos(depTramos);
      if (errs.length) {
        toast.error(errs[0]);
        return;
      }
    }
    if (depForm.usarHorario) {
      const avisoObjetivo = mensajeObjetivoFlexible(depModo);
      if (avisoObjetivo && !window.confirm(avisoObjetivo)) return;
    }
    try {
      const idsAplicar = depForm.empleadosAplicarIds || [];
      const payload = {
        nombre: depForm.nombre,
        centro_id: depForm.centro_id ? Number(depForm.centro_id) : null,
        color: depForm.color,
        horario: depForm.usarHorario ? horarioParaGuardarTramos(depTramos, depModo) : null,
        motivo_horario: depForm.motivo_horario.trim(),
        aplicar_a_empleados_con_horario_propio: false,
        empleados_aplicar_horario_ids: idsAplicar,
      };
      if (editDepId) {
        const { data: res } = await api.put(`/departamentos/${editDepId}`, payload);
        const partes = [];
        if (res.fecha_vigencia_horario) {
          const f = new Date(`${res.fecha_vigencia_horario}T12:00:00`).toLocaleDateString("es-ES");
          partes.push(`El nuevo horario del departamento aplica a partir del ${f}`);
        } else {
          partes.push("Departamento actualizado");
        }
        if (res.empleados_pasan_a_heredar) {
          partes.push(`${res.empleados_pasan_a_heredar} empleado(s) pasan a heredar el horario del departamento`);
        } else if (empleadosConHorarioPropioDep.length && !idsAplicar.length) {
          partes.push(
            `${empleadosConHorarioPropioDep.length} con horario propio no cambian (márcalos abajo si quieres que hereden)`,
          );
        }
        toast.success(partes.join(". ") + ".");
      } else {
        await api.post("/departamentos", payload);
        toast.success("Departamento creado");
      }
      setDlgDep(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const savePuesto = async () => {
    try {
      const payload = {
        nombre: puestoForm.nombre,
        departamento_id: Number(puestoForm.departamento_id),
        color: puestoForm.color,
      };
      if (editPuestoId) {
        await api.put(`/puestos/${editPuestoId}`, payload);
        toast.success("Puesto actualizado");
      } else {
        await api.post("/puestos", payload);
        toast.success("Puesto creado");
      }
      setDlgPuesto(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteCentro = async (centro) => {
    if (!centro.id || !window.confirm(`¿Eliminar el centro "${centro.nombre}"?`)) return;
    try {
      await api.delete(`/centros/${centro.id}`);
      toast.success("Centro eliminado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteDep = async (dep) => {
    if (!window.confirm(`¿Eliminar el departamento "${dep.nombre}"?`)) return;
    try {
      await api.delete(`/departamentos/${dep.id}`);
      toast.success("Departamento eliminado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePuesto = async (puesto) => {
    if (!window.confirm(`¿Eliminar el puesto "${puesto.nombre}"?`)) return;
    try {
      await api.delete(`/puestos/${puesto.id}`);
      toast.success("Puesto eliminado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) {
    return <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />;
  }

  const centrosReales = data.centros.filter((c) => c.id != null);
  const todos = [
    ...data.centros.flatMap((c) => c.departamentos.flatMap((d) => [...d.puestos.flatMap((p) => p.trabajadores), ...d.sin_puesto])),
    ...data.sin_asignar,
  ];
  const conGps = todos.filter((t) => t.latitud != null);
  const centroMapa = conGps.length
    ? [conGps.reduce((a, t) => a + t.latitud, 0) / conGps.length, conGps.reduce((a, t) => a + t.longitud, 0) / conGps.length]
    : [40.4168, -3.7038];

  return (
    <div className="space-y-6" data-testid="organizacion-page">
      <div className="rounded-2xl p-6 shadow-lg" style={orgHeaderStyle()}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
              <Network className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-display text-3xl font-semibold tracking-tight">Organización</h1>
              <p className="mt-0.5 text-sm text-white/80">Centro → Departamento → Puesto · Horarios y equipo</p>
            </div>
          </div>
          {esAdmin && (
            <Button onClick={() => abrirCentro()} className="gap-1.5 bg-white/20 text-white hover:bg-white/30" data-testid="crear-centro-btn">
              <Plus className="h-4 w-4" />
              Nuevo centro
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="arbol">
        <TabsList className="rounded-lg bg-white shadow-sm">
          <TabsTrigger value="arbol" data-testid="tab-arbol">Estructura</TabsTrigger>
          <TabsTrigger value="mapa" data-testid="tab-mapa">Mapa</TabsTrigger>
        </TabsList>

        <TabsContent value="arbol" className="mt-4 space-y-5" data-testid="arbol-vista">
          {data.centros.map((centroItem, ci) => (
            <div key={centroItem.id ?? `sc-${ci}`} className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center gap-3 px-5 py-4 text-white" style={{ background: `linear-gradient(135deg, ${centroItem.color}, ${centroItem.color}cc)` }}>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                  <Building2 className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-display text-lg font-semibold">{centroItem.nombre}</h2>
                  {fmtDireccionCompleta(centroItem) && (
                    <p className="truncate text-xs text-white/80">{fmtDireccionCompleta(centroItem)}</p>
                  )}
                </div>
                <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium">
                  {centroItem.num_trabajadores ?? 0} pers.
                </span>
                {esAdmin && centroItem.id && (
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs text-white/90 hover:bg-white/20" onClick={() => abrirDep(centroItem.id)}>
                      <Plus className="h-3 w-3" /> Depto
                    </Button>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-white/80 hover:bg-white/20" onClick={() => abrirCentro(centroItem)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-white/80 hover:bg-white/20 disabled:opacity-30"
                      disabled={(centroItem.num_trabajadores ?? 0) > 0}
                      title={(centroItem.num_trabajadores ?? 0) > 0 ? "Hay trabajadores asignados" : "Eliminar centro"}
                      onClick={() => deleteCentro(centroItem)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-4 p-4">
                {centroItem.departamentos.map((dep) => (
                  <div key={dep.id} className="rounded-xl border p-4" style={{ borderColor: `${dep.color}44`, backgroundColor: `${dep.color}08` }}>
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: dep.color }} />
                          <h3 className="font-display font-semibold" style={{ color: dep.color }}>{dep.nombre}</h3>
                          <span className="text-xs text-zinc-400">· {dep.num_trabajadores} pers.</span>
                        </div>
                        <p className="mt-0.5 pl-5 text-xs font-medium text-indigo-700">{dep.resumen_modo_horario || resumenModoHorario(dep.horario_efectivo || dep.horario)}</p>
                        <p className="mt-0.5 pl-5 text-xs text-zinc-500">{resumenHorarioTramos(dep.horario_efectivo || dep.horario)}</p>
                      </div>
                      {esAdmin && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => abrirDep(centroItem.id, dep)}>
                            <Pencil className="h-3 w-3" /> Editar
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => abrirPuesto(dep.id)}>
                            <Plus className="h-3 w-3" /> Puesto
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-red-600 disabled:opacity-40"
                            disabled={dep.num_trabajadores > 0}
                            title={dep.num_trabajadores > 0 ? "Hay trabajadores asignados" : "Eliminar departamento"}
                            onClick={() => deleteDep(dep)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {dep.puestos.map((puesto) => (
                      <div key={puesto.id} className="mb-3 ml-4 border-l-2 pl-4" style={{ borderColor: `${puesto.color}55` }}>
                        <div className="mb-1 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-3.5 w-3.5" style={{ color: puesto.color }} />
                            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: puesto.color }}>{puesto.nombre}</span>
                            <span className="text-[10px] text-zinc-400">{puesto.num_trabajadores} pers.</span>
                          </div>
                          {esAdmin && (
                            <div className="flex gap-1">
                              <button type="button" className="rounded p-1 text-zinc-400 hover:text-zinc-700" onClick={() => abrirPuesto(dep.id, puesto)}>
                                <Pencil className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                className="rounded p-1 text-zinc-400 hover:text-red-500 disabled:opacity-30"
                                disabled={puesto.num_trabajadores > 0}
                                onClick={() => deletePuesto(puesto)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                        {puesto.trabajadores.map((t) => (
                          <FilaTrabajador key={t.id} t={t} accent={puesto.color} />
                        ))}
                        {puesto.trabajadores.length === 0 && (
                          <p className="py-1 text-xs text-zinc-400">Sin personas en este puesto.</p>
                        )}
                      </div>
                    ))}

                    {dep.sin_puesto.length > 0 && (
                      <div className="ml-4 border-l-2 border-dashed border-zinc-200 pl-4">
                        <p className="mb-1 text-[10px] font-semibold uppercase text-zinc-400">Sin puesto</p>
                        {dep.sin_puesto.map((t) => (
                          <FilaTrabajador key={t.id} t={t} accent={dep.color} />
                        ))}
                      </div>
                    )}

                    {dep.puestos.length === 0 && dep.sin_puesto.length === 0 && (
                      <p className="text-sm text-zinc-400">Sin personas asignadas.</p>
                    )}
                  </div>
                ))}
                {centroItem.departamentos.length === 0 && (
                  <p className="py-4 text-center text-sm text-zinc-400">
                    Sin departamentos.{esAdmin && centroItem.id && " Añade uno con el botón Depto."}
                  </p>
                )}
              </div>
            </div>
          ))}

          {data.sin_asignar.length > 0 && (
            <div className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 p-4">
              <h3 className="mb-2 font-display font-medium text-zinc-500">Sin departamento</h3>
              {data.sin_asignar.map((t) => (
                <FilaTrabajador key={t.id} t={t} accent="#94a3b8" />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mapa" className="mt-4" data-testid="mapa-vista">
          {conGps.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center">
              <MapPin className="h-8 w-8 text-indigo-300" strokeWidth={1.5} />
              <p className="text-sm text-zinc-400">Aún no hay fichajes con ubicación GPS.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-zinc-200 shadow-sm">
              <MapContainer center={centroMapa} zoom={12} style={{ height: "560px", width: "100%", zIndex: 0 }}>
                <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {conGps.map((t) => (
                  <Marker key={t.id} position={[t.latitud, t.longitud]}>
                    <Popup>
                      <p className="font-semibold">{t.nombre}</p>
                      <p>{ESTADO[t.estado].label}</p>
                      <p className="text-xs">GPS: {fmtFecha(t.gps_timestamp)} {fmtHora(t.gps_timestamp)}</p>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={dlgCentro} onOpenChange={setDlgCentro}>
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editCentroId ? "Editar centro" : "Nuevo centro de trabajo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={centroForm.nombre} onChange={(e) => setCentroForm({ ...centroForm, nombre: e.target.value })} data-testid="centro-nombre" />
            </div>
            <div className="space-y-1.5">
              <Label>Dirección (calle, número…)</Label>
              <Input value={centroForm.direccion} onChange={(e) => setCentroForm({ ...centroForm, direccion: e.target.value })} placeholder="Calle, número, nave…" />
            </div>
            <DireccionEspanaFields
              value={centroForm}
              onChange={(next) => setCentroForm({
                ...centroForm,
                codigo_postal: next.codigo_postal || "",
                poblacion: next.poblacion || "",
                provincia: next.provincia || "",
              })}
            />
            <div className="space-y-1.5">
              <Label>Color</Label>
              <SelectorColor value={centroForm.color} onChange={(c) => setCentroForm({ ...centroForm, color: c })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2.5">
              <div>
                <p className="text-sm font-medium">Horario base del centro</p>
                <p className="text-xs text-zinc-500">Aplica a departamentos y trabajadores que no tengan horario propio</p>
              </div>
              <Switch
                checked={centroForm.usarHorario}
                onCheckedChange={(v) => setCentroForm({ ...centroForm, usarHorario: v })}
              />
            </div>
            {centroForm.usarHorario && (
              <>
                <HorarioModoConfig config={centroModo} onChange={setCentroModo} plantillas={turnos} />
                {centroModo.modo !== MODO_PATRON_TURNOS && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      {centroModo.modo === "flexible" ? "Ventanas diarias de fichaje (por tramos de fechas)" : "Horario por tramos de fechas"}
                    </Label>
                    <p className="text-xs text-amber-800/90 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                      Al guardar, los tramos pasados se conservan. El nuevo horario aplica como mínimo el día siguiente
                      al fin de la jornada vigente. Los departamentos que hereden este horario se actualizarán.
                    </p>
                    <HorarioTramosEditor
                      tramos={centroTramos}
                      onChange={setCentroTramos}
                      prefix="centro-tramos"
                      plantillas={turnos}
                      modo={centroModo.modo}
                    />
                  </div>
                )}
                {editCentroId && (
                  <div className="space-y-1.5">
                    <Label>Motivo del cambio (opcional)</Label>
                    <Input
                      value={centroForm.motivo_horario}
                      onChange={(e) => setCentroForm({ ...centroForm, motivo_horario: e.target.value })}
                      placeholder="Ej. Nuevo turno de mañana"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveCentro} disabled={!centroForm.nombre.trim()} data-testid="centro-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgDep} onOpenChange={setDlgDep}>
        <DialogContent className="flex max-h-[min(92vh,900px)] w-[95vw] max-w-4xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-zinc-100 px-6 py-4 pr-12 text-left">
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              {editDepId ? "Editar departamento" : "Nuevo departamento"}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={depForm.nombre} onChange={(e) => setDepForm({ ...depForm, nombre: e.target.value })} data-testid="dep-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Centro de trabajo</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"
                  value={depForm.centro_id}
                  onChange={(e) => {
                    const cid = e.target.value;
                    setDepForm({ ...depForm, centro_id: cid });
                    if (!depForm.usarHorario) {
                      const origen = (data?.centros || []).find((x) => x.id === Number(cid))?.horario || null;
                      setDepTramos(tramosFromHorario(origen));
                      setDepModo(modoConfigFromHorario(origen));
                    }
                  }}
                >
                  <option value="">Sin centro</option>
                  {centrosReales.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Color</Label>
                <SelectorColor size="sm" value={depForm.color} onChange={(c) => setDepForm({ ...depForm, color: c })} />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-violet-100 bg-violet-50/40 px-3 py-2.5">
              <div>
                <p className="text-sm font-medium">Horario propio del departamento</p>
                <p className="text-xs text-zinc-500">
                  Si está desactivado, hereda el del centro. Al activarlo se copia como borrador; cámbialo a flexible y sustituye el tramo futuro.
                </p>
              </div>
              <Switch checked={depForm.usarHorario} onCheckedChange={onToggleHorarioDep} />
            </div>
            {!depForm.usarHorario && (
              <div className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                <p className="font-medium text-zinc-800">Horario heredado del centro</p>
                {horarioCentroSeleccionado(depForm.centro_id) ? (
                  <>
                    <p className="mt-0.5 text-indigo-700">{resumenModoHorario(horarioCentroSeleccionado(depForm.centro_id))}</p>
                    <p>{resumenHorarioTramos(horarioCentroSeleccionado(depForm.centro_id))}</p>
                  </>
                ) : (
                  <p className="mt-0.5 text-amber-700">El centro no tiene horario base configurado.</p>
                )}
              </div>
            )}
            {depForm.usarHorario && (
              <>
                <HorarioModoConfig config={depModo} onChange={onChangeDepModo} plantillas={turnos} />
                {depModo.modo === MODO_FLEXIBLE && (
                  <div className="rounded-lg border border-teal-200 bg-teal-50/50 px-3 py-2.5 text-xs text-teal-950">
                    <p className="font-medium">
                      ¿Sustituir el tramo desde {new Date(`${fechaSugeridaFlexible}T12:00:00`).toLocaleDateString("es-ES")} por flexible?
                    </p>
                    <p className="mt-0.5 text-teal-800/90">
                      Conserva lo anterior hasta el día previo e inserta ventana 06:00–22:00 Lun–Vie. Las horas se acumulan hacia el objetivo.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-2 border-teal-300"
                      onClick={() => sustituirFlexibleDesde(fechaSugeridaFlexible)}
                      data-testid="dep-sustituir-flexible-oct"
                    >
                      Sustituir desde esa fecha
                    </Button>
                  </div>
                )}
                {depModo.modo !== MODO_PATRON_TURNOS && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      {depModo.modo === "flexible" ? "Ventanas diarias de fichaje (por tramos de fechas)" : "Horario por tramos de fechas"}
                    </Label>
                    <p className="text-xs text-amber-800/90 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                      Al guardar, los tramos pasados se conservan. El nuevo horario aplica como mínimo el día siguiente
                      al fin de la jornada vigente (si hay turno nocturno que acaba al día siguiente, será pasado mañana
                      como muy pronto). Se notificará al equipo con la fecha exacta.
                    </p>
                    <HorarioTramosEditor
                      tramos={depTramos}
                      onChange={setDepTramos}
                      prefix="dep-tramos"
                      plantillas={turnos}
                      modo={depModo.modo}
                    />
                  </div>
                )}
              </>
            )}
            {editDepId && (
              <div className="space-y-1.5">
                <Label>Motivo del cambio (opcional)</Label>
                <Input
                  value={depForm.motivo_horario}
                  onChange={(e) => setDepForm({ ...depForm, motivo_horario: e.target.value })}
                  placeholder="Ej. Ajuste de horario de verano"
                />
              </div>
            )}
            {editDepId && empleadosConHorarioPropioDep.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium">
                    Empleados con horario propio ({empleadosConHorarioPropioDep.length})
                  </p>
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      className="underline underline-offset-2 hover:text-amber-700"
                      onClick={() => toggleTodosEmpleadosAplicar(true)}
                      data-testid="dep-aplicar-todos"
                    >
                      Marcar todos
                    </button>
                    <span className="text-amber-300">·</span>
                    <button
                      type="button"
                      className="underline underline-offset-2 hover:text-amber-700"
                      onClick={() => toggleTodosEmpleadosAplicar(false)}
                      data-testid="dep-aplicar-ninguno"
                    >
                      Ninguno
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-xs text-amber-800/90">
                  Marca a quién quieras que deje el horario individual y herede el del departamento. El resto no cambia.
                </p>
                <ul className="mt-2 space-y-1.5">
                  {empleadosConHorarioPropioDep.map((emp) => {
                    const checked = (depForm.empleadosAplicarIds || []).includes(emp.id);
                    return (
                      <li key={emp.id}>
                        <label className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-0.5 hover:bg-amber-100/70">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEmpleadoAplicar(emp.id)}
                            data-testid={`dep-aplicar-emp-${emp.id}`}
                          />
                          <span>{emp.nombre}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter className="shrink-0 border-t border-zinc-100 bg-white px-6 py-3 sm:justify-end">
            <Button onClick={saveDep} disabled={!depForm.nombre.trim()} data-testid="dep-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgPuesto} onOpenChange={setDlgPuesto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              {editPuestoId ? "Editar puesto" : "Nuevo puesto"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre del puesto</Label>
              <Input value={puestoForm.nombre} onChange={(e) => setPuestoForm({ ...puestoForm, nombre: e.target.value })} placeholder="Ej.: Operario, Encargado…" />
            </div>
            <div className="space-y-1.5">
              <Label>Color</Label>
              <SelectorColor value={puestoForm.color} onChange={(c) => setPuestoForm({ ...puestoForm, color: c })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={savePuesto} disabled={!puestoForm.nombre.trim()}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
